import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, Users, Flame, Beef, Wheat, Droplet, ListChecks, ChefHat, Loader2, AlertTriangle, Sparkles, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SharedRecipePage = () => {
  const { token } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/personal-recipes/shared/${token}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setRecipe)
      .catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6" data-testid="shared-recipe-error">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">Cette recette n'existe pas ou n'est plus disponible.</p>
          <Link to="/" className="text-primary font-semibold hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="shared-recipe-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const m = recipe.macros;

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="shared-recipe-page">
      <div className="relative h-72 sm:h-96 bg-zinc-800">
        {recipe.image_url && <img src={recipe.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-black/40 to-black/20" />
        <div className="absolute bottom-0 left-0 right-0 p-6 max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-primary/80 text-white mb-3"><Sparkles className="w-3 h-3" /> Recette partagée par un membre Body-Up</span>
          <h1 className="text-white text-2xl sm:text-4xl font-black leading-tight">{recipe.name}</h1>
          <div className="flex items-center gap-4 mt-3 text-sm text-zinc-300">
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {recipe.prep_min} min</span>
            <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" /> {recipe.servings} portion(s)</span>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="grid grid-cols-4 gap-2.5 mb-8">
          {[['calories', Flame, m.calories, 'text-primary'], ['protein', Beef, m.protein, 'text-pink-400'], ['carbs', Wheat, m.carbs, 'text-yellow-400'], ['fat', Droplet, m.fat, 'text-emerald-400']].map(([k, Icon, val, color]) => (
            <div key={k} className="rounded-xl bg-zinc-900 border border-white/10 p-3 text-center">
              <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
              <p className="font-bold text-white text-sm">{Math.round(val)}{k === 'calories' ? '' : 'g'}</p>
              <p className="text-[10px] text-zinc-500">{k === 'calories' ? 'kcal' : ''}</p>
            </div>
          ))}
        </div>

        {recipe.ingredients?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-base font-bold text-white mb-3 flex items-center gap-1.5"><ListChecks className="w-4 h-4 text-primary" /> Ingrédients</h2>
            <ul className="space-y-1.5" data-testid="shared-recipe-ingredients">
              {recipe.ingredients.map((ing, i) => <li key={i} className="text-sm text-zinc-300 border-b border-white/5 pb-1.5">{ing}</li>)}
            </ul>
          </div>
        )}

        {recipe.steps?.length > 0 && (
          <div className="mb-10">
            <h2 className="text-base font-bold text-white mb-3 flex items-center gap-1.5"><ChefHat className="w-4 h-4 text-primary" /> Préparation</h2>
            <ol className="space-y-3" data-testid="shared-recipe-steps">
              {recipe.steps.map((s, i) => (
                <li key={i} className="flex gap-3 text-sm text-zinc-300">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-primary/20 border border-primary/40 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="pt-0.5">{s}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-pink-500/10 border border-primary/30 p-6 text-center">
          <p className="text-white font-bold mb-1">Envie d'un plan alimentaire sur-mesure comme celui-ci ?</p>
          <p className="text-sm text-zinc-400 mb-4">Body-Up génère votre plan personnalisé, calibré sur vos objectifs, avec vos propres recettes.</p>
          <Link to="/calculateur-calories" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-105 transition-transform" data-testid="shared-recipe-cta">
            Découvrir Body-Up <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <p className="text-center mt-6">
          <Link to="/recettes-communaute" className="text-sm text-zinc-500 hover:text-primary transition-colors" data-testid="shared-recipe-gallery-link">Voir d'autres recettes de la communauté →</Link>
        </p>
      </div>
    </div>
  );
};

export default SharedRecipePage;
